import './RefinanciarWizard.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { BackButton, ContinueButton } from '../../components/buttons/Buttons';
import EntradaMoneda from '../../components/currencyInput/EntradaMoneda';
import FileLoader from '../../components/fileLoader/FileLoader';
import ConsentBox from '../../components/consentBox/ConsentBox';
import Popup from '../../components/popup/Popup';
import { ReactComponent as CheckMarkIcon } from '../../img/check-mark.svg';
import { ReactComponent as TrashIcon } from '../../img/trash.svg';
import { confirmOption, simulateCredit, updateClientData } from '../../services/apiService';
import {
  getFromSessionStorage,
  removeSessionStorage,
  saveToSessionStorage,
} from '../../services/storageService';
import { getCurrentDate } from '../../utils/formUtils';
import useTrackEvent from '../../utils/trackEvent';
import values from '../../utils/values.json';

const MONTO_DEUDA_TEXT =
  'El monto de la deuda es el saldo que aún te queda por pagar de tu crédito automotriz. No corresponde al valor total del vehículo, sino al precio del auto menos el pie que pagaste al inicio y las cuotas que ya has abonado.';

const ConDetalle = ({ onBack, data }) => {
  const hasPushedPageView = useRef(false);
  const { pushCustomEvent } = useTrackEvent();
  const navigate = useNavigate();
  const { isMeliTenant } = useTenant();
  const { flags, isLoadingFlags } = useFeatureFlags();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedFile, setLoadedFile] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const isConsentEnabled = flags.isConsentEnabled;
  const executiveRut = !isLoadingFlags ? flags.executiveRut : undefined;
  const sellerRut = !isLoadingFlags ? flags.sellerRut : undefined;
  const dealerRut = !isLoadingFlags ? flags.dealerRut : undefined;
  const storeCode = !isLoadingFlags ? flags.storeCode : undefined;
  const storeCodeMeli = !isLoadingFlags ? flags.storeCodeMeli : undefined;
  const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  const storedFormData = JSON.parse(sessionStorage.getItem('formData')) || {};
  const [formData, setFormData] = useState({
    id: parsedData?.id || storedFormData?.id,
    rut: parsedData?.rut || storedFormData?.rut || '',
    dv: parsedData?.dv || storedFormData?.dv || '',
    name: parsedData?.name || storedFormData?.name || '',
    surnames: parsedData?.surnames || storedFormData?.surnames || '',
    phone: parsedData?.phone || storedFormData?.phone || '',
    email: parsedData?.email || storedFormData?.email || '',
    typeFinancing: parsedData?.typeFinancing || storedFormData?.typeFinancing || '',
    type: parsedData?.type || storedFormData?.type || 'CF',
    workerType: parsedData?.workerType || storedFormData?.workerType || '',
    salary: parsedData?.salary || storedFormData?.salary || 0,
    startWorkingDate: parsedData?.startWorkingDate || storedFormData?.startWorkingDate || '',
    carValue: parsedData?.carValue || storedFormData?.carValue || '',
    footAmount: parsedData?.footAmount || storedFormData?.footAmount || '',
    fee: parsedData?.fee || storedFormData?.fee || '',
    caryear: parsedData?.caryear || storedFormData?.caryear || '',
    registrationNumber: parsedData?.registrationNumber || storedFormData?.registrationNumber || '',
    brand: parsedData?.brand || storedFormData?.brand || '',
    model: parsedData?.model || storedFormData?.model || '',
    amountFinanced: parsedData?.amountFinanced || storedFormData?.amountFinanced || '',
    paidFees: parsedData?.paidFees || storedFormData?.paidFees || '',
    entidad: parsedData?.entidad || storedFormData?.entidad || values.institucion?.[0]?.value || '',
    consentChecked: parsedData?.consentChecked || storedFormData?.consentChecked || false,
  });

  useEffect(() => {
    if (!hasPushedPageView.current) {
      pushCustomEvent('vpv', {
        flujo: 'Refinanciamiento',
        step: '2',
        page: '/refinancia-tu-credito/info-vehiculo',
        PageName: 'Información de vehículo',
        estado: 'conoce',
        version: 'v2',
      });

      hasPushedPageView.current = true;
    }
  }, []);

  const getYearRange = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // Enero = 0, Septiembre = 8

    const endYear = currentMonth >= 8 ? currentYear + 1 : currentYear;
    const startYear = endYear - 7; // 8 años en total (incluyendo endYear)

    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i); // Invertir el orden
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setFormData((prevData) => ({ ...prevData, caryear: year }));
  };

  const cleanCurrencyValue = (value) => {
    return parseFloat(value.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    if (['amountFinanced', 'footAmount'].includes(name)) {
      const rawValue = cleanCurrencyValue(value).toString();

      let maxLength = 15;
      if (name === 'amountFinanced') maxLength = 9;
      if (name === 'footAmount') maxLength = 8;

      newValue = rawValue.slice(0, maxLength);
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));
  };

  const handleFeesInputChange = (e) => {
    const { name, value } = e.target;
    const limitedValue = value.slice(0, 2);
    setFormData((prevData) => ({ ...prevData, [name]: limitedValue }));
  };

  const handleTooltipClick = () => {
    setShowTooltip(!showTooltip);
  };

  const handleError = (error) => {
    setError(error);
  };

  const handleLoadFile = (file) => {
    setLoadedFile(file);
  };

  const handleConsentChange = (e) => {
    setFormData((prevData) => ({ ...prevData, consentChecked: e.target.checked }));
  };

  const validateForm = () => {
    const validFees = [6, 12, 18, 24, 30, 36, 42, 48, 60];
    const fee = parseInt(formData.fee);
    if (!fee || !validFees.includes(fee)) {
      return 'Por favor, selecciona un número de cuotas totales válido (6, 12, 18, 24, 30, 36, 42, 48, 60).';
    }

    const paidFees = parseInt(formData.paidFees);

    if (isNaN(paidFees) || paidFees === null || paidFees === undefined) {
      return 'Por favor, selecciona un número de cuotas pagadas válido.';
    }

    if (paidFees > fee) {
      return 'Las cuotas pagadas no pueden ser mayores que las cuotas totales.';
    }

    const currentYear = parseInt(getCurrentDate().slice(0, 4));
    if (!formData.caryear || formData.caryear < 2000 || formData.caryear > currentYear) {
      return `Por favor, selecciona un año del vehículo válido entre 2000 y ${currentYear}.`;
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const eventData = {
      flujo: 'Refinanciamiento',
      button_step_value: '2',
      step: '2',
      page: '/refinancia-tu-credito/info-vehiculo',
      PageName: 'Información de vehículo',
      button_step_name: 'continuar',
      estado: 'conoce',
      version: 'v2',
    };

    pushCustomEvent('event_buttons', eventData);
    setIsLoading(true);
    setError(null);

    try {
      const updatedData = {
        ...formData,
        model: formData.model,
        brand: formData.brand,
        caryear: formData.caryear,
        fee: formData.fee,
        paidFees: formData.paidFees,
      };

      saveToSessionStorage('formData', updatedData);
      const simulationDataStored = getFromSessionStorage('simulationData');
      let simulationData = simulationDataStored ?? null;

      await updateClientData(updatedData.id, updatedData);

      if (!simulationData) {
        simulationData = await simulateCredit({
          ...formData,
          sourceName: isMeliTenant ? 'MercadoLibre' : 'Crediautos',
          channelRequest: isMeliTenant
            ? 'Mercadolibre'
            : formData?.typeFinancing
              ? `${formData.typeFinancing}_v2`
              : null,
          storeCode: isMeliTenant ? storeCodeMeli : storeCode,
          executiveRut,
          sellerRut,
          dealerRut,
        });

        const simulation = simulationData?.simulationData;
        const creditSimulation = simulation?.creditSimulation;

        if (Array.isArray(creditSimulation) && creditSimulation.length > 0) {
          const [firstOption] = creditSimulation;

          formData.option = firstOption;
          formData.simulationId = simulation.id;

          saveToSessionStorage('simulationData', simulationData);
        }
      }

      await confirmOption({
        ...formData,
        storeCode: isMeliTenant ? storeCodeMeli : storeCode,
        executiveRut,
        sellerRut,
        dealerRut,
      });

      eventData.button_step_name = 'continuar OK';
      pushCustomEvent('event_buttons', eventData);

      removeSessionStorage('simulationData');

      navigate('/refinancia-tu-credito/solicitud-exitosa', {
        state: { currentStep: 3, estado: 'conoce' },
      });
    } catch (error) {
      eventData.button_step_name = 'continuar Error';
      pushCustomEvent('event_buttons', eventData);
      setError('¡Ups! Algo salió mal. Intenta nuevamente en unos segundos.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="info-credito">
      <h1>Información del crédito</h1>
      <p>Completa los datos de tu crédito</p>
      <form className="form-container" onSubmit={handleSubmit}>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Entidad financiera *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              onChange={handleInputChange}
              name="entidad"
              value={formData.entidad || ''}
              className="form-input-column"
              required
            >
              {values.institucion.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Monto de la deuda *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              id="amountFinanced"
              name="amountFinanced"
              type="text"
              value={formData.amountFinanced || ''}
              onChange={handleInputChange}
              placeholder="$25.490.000"
              className="form-input-column"
              required
            />
            <span className="tooltip-trigger" onClick={() => handleTooltipClick()}>
              i
            </span>
          </div>
        </div>

        {showTooltip && (
          <Popup
            title={'¿Qué es el monto de la deuda?'}
            closePopup={handleTooltipClick}
            textContent={MONTO_DEUDA_TEXT}
          />
        )}

        <div className="cuotas-container">
          <div className="formControl-root">
            <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
              Cuotas pagadas *
            </label>
            <div className="outlinedInput-root textField-root inputBase-root">
              <input
                type="text"
                className="form-input-column"
                required
                id="paidFees"
                placeholder="3"
                value={formData.paidFees}
                onChange={handleFeesInputChange}
                name="paidFees"
              />
            </div>
          </div>
          <div className="formControl-root">
            <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
              Cuotas totales *
            </label>
            <div className="outlinedInput-root textField-root inputBase-root">
              <input
                type="text"
                className="form-input-column"
                id="fee"
                name="fee"
                placeholder="36"
                value={formData.fee}
                onChange={handleFeesInputChange}
                required
              />
            </div>
          </div>
        </div>
        <div className="vehiculo-container">
          <div className="formControl-root">
            <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
              Marca *
            </label>
            <div className="outlinedInput-root textField-root inputBase-root">
              <input
                type="text"
                id="brand"
                name="brand"
                placeholder="Ej: Maxus"
                className="form-input-column"
                value={formData.brand}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          <div className="formControl-root">
            <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
              Modelo *
            </label>
            <div className="outlinedInput-root textField-root inputBase-root">
              <input
                type="text"
                id="model"
                name="model"
                placeholder="Ej: T60"
                className="form-input-column"
                onChange={handleInputChange}
                value={formData.model}
                required
              />
            </div>
          </div>
        </div>

        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Año del vehículo *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="caryear"
              className="form-input-column"
              onChange={(e) => handleYearChange(e.target.value)}
              value={selectedYear} // Vincular el valor con el estado
              required
            >
              <option value="" disabled hidden>
                Seleccionar año
              </option>
              {getYearRange().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
        <FileLoader handleLoadFile={handleLoadFile} handleError={handleError} />

        {!isLoadingFlags && isConsentEnabled && (
          <ConsentBox checked={formData.consentChecked} onChange={handleConsentChange} />
        )}

        {loadedFile && !error ? (
          <div className="loaded-document">
            <CheckMarkIcon fill="#004e9c" width="30" height="30" />
            <span>{loadedFile.name}</span>
            <TrashIcon
              style={{ cursor: 'pointer' }}
              onClick={() => setLoadedFile(null)}
              width="30"
              height="30"
            />
          </div>
        ) : (
          /* Mostrar mensaje de error si existe */
          error && <div className="error-msg">{error}</div>
        )}

        <div className="nav-button-container">
          <BackButton onClick={() => onBack()} />
          <ContinueButton
            type="submit"
            isLoading={isLoading}
            isMeliTenant={isMeliTenant}
            disabled={!formData.consentChecked}
          />
        </div>
      </form>
    </div>
  );
};

export default ConDetalle;
